import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useServiciosPorEmpresa } from '@/components/hooks/useServicios';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingBono?: any | null;
  onSaved: () => void;
  crearBono: (bono: any) => Promise<any>;
  actualizarBono: (id: string, data: any) => Promise<any>;
}

interface BonoForm {
  idServicio: string;
  nombre: string;
  descripcion: string;
  cantidad: number;
  precio: number;
  duracion: number;
  activo: boolean;
}

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
      {label}
    </label>
    {children}
  </div>
);

export const CreateBonoDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  editingBono,
  onSaved,
  crearBono,
  actualizarBono,
}) => {
  const { t } = useTranslation();
  const empresaId = localStorage.getItem('id_empresa') || '';
  const usuarioId = localStorage.getItem('id_usuario') || '';

  const { servicios } = useServiciosPorEmpresa(empresaId);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<BonoForm>({
    idServicio: '',
    nombre: '',
    descripcion: '',
    cantidad: 1,
    precio: 0,
    duracion: 30,
    activo: true,
  });

  useEffect(() => {
    if (editingBono) {
      setForm({
        idServicio: editingBono.id_servicio || '',
        nombre: editingBono.nombre_bono || '',
        descripcion: editingBono.descripcion || '',
        cantidad: editingBono.cantidad_servicios ?? 1,
        precio: editingBono.precio_total ?? 0,
        duracion: editingBono.duracion_dias ?? 30,
        activo: editingBono.activo ?? true,
      });
    } else {
      setForm({
        idServicio: '',
        nombre: '',
        descripcion: '',
        cantidad: 1,
        precio: 0,
        duracion: 30,
        activo: true,
      });
    }
  }, [editingBono, open]);

  const handleSave = async () => {
    if (!empresaId) return toast.error(t('createBonoDialog.errorNoEmpresa'));
    if (!form.idServicio)
      return toast.error(t('createBonoDialog.errorNoService'));
    if (!form.nombre.trim())
      return toast.error(t('createBonoDialog.errorNoName'));

    setLoading(true);
    try {
      if (editingBono) {
        const result = await actualizarBono(editingBono.id_bono, {
          id_servicio: form.idServicio,
          nombre: form.nombre,
          descripcion: form.descripcion,
          cantidad_servicios: form.cantidad,
          precio_total: form.precio,
          duracion_dias: form.duracion,
          activo: form.activo,
          usuario_edicion: usuarioId,
        });
        if (!result.ok) throw result.error;
        toast.success(t('createBonoDialog.successUpdate'));
      } else {
        await crearBono({
          id_servicio: form.idServicio,
          nombre: form.nombre,
          descripcion: form.descripcion,
          cantidad_servicios: form.cantidad,
          precio_total: form.precio,
          duracion_dias: form.duracion,
          usuario_creacion: usuarioId,
          activo: form.activo,
        });
        toast.success(t('createBonoDialog.successCreate'));
      }
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error('Error guardando bono:', error);
      toast.error(t('createBonoDialog.errorSave'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {editingBono
                ? t('createBonoDialog.titleEdit')
                : t('createBonoDialog.titleCreate')}
            </DialogTitle>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('createBonoDialog.description')}
            </p>
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <Field label={t('createBonoDialog.labelService')}>
              <Select
                value={form.idServicio}
                onValueChange={(v) => setForm({ ...form, idServicio: v })}
              >
                <SelectTrigger className="h-10 w-full text-sm dark:bg-white/5 dark:border-white/10 dark:text-white">
                  <SelectValue
                    placeholder={t('createBonoDialog.placeholderService')}
                  />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {servicios.map((s) => (
                    <SelectItem
                      key={s.id_servicio}
                      value={s.id_servicio}
                      className="text-sm"
                    >
                      {s.servicio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.idServicio &&
                (() => {
                  const svc = servicios.find(
                    (s) => s.id_servicio === form.idServicio
                  );
                  if (!svc) return null;
                  const h = Math.floor(svc.duracion_minutos / 60);
                  const m = svc.duracion_minutos % 60;
                  return (
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {h > 0 && `${h}h `}
                      {m > 0 && `${m}min`} — {(svc.precio ?? 0).toFixed(2)} €
                    </p>
                  );
                })()}
            </Field>

            <Field label={t('createBonoDialog.labelName')}>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder={t('createBonoDialog.placeholderName')}
                className="h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </Field>

            <Field label={t('createBonoDialog.labelDescription')}>
              <textarea
                value={form.descripcion}
                onChange={(e) =>
                  setForm({ ...form, descripcion: e.target.value })
                }
                rows={3}
                placeholder={t('createBonoDialog.placeholderDescription')}
                className="w-full border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#103254] dark:focus:ring-blue-400"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('createBonoDialog.labelQuantity')}>
                <Input
                  type="number"
                  min={1}
                  value={form.cantidad}
                  onChange={(e) =>
                    setForm({ ...form, cantidad: Number(e.target.value) })
                  }
                  className="h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </Field>
              <Field label={t('createBonoDialog.labelPrice')}>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.precio}
                  onChange={(e) =>
                    setForm({ ...form, precio: Number(e.target.value) })
                  }
                  className="h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </Field>
            </div>

            <Field label={t('createBonoDialog.labelDuration')}>
              <Input
                type="number"
                min={1}
                value={form.duracion}
                onChange={(e) =>
                  setForm({ ...form, duracion: Number(e.target.value) })
                }
                className="h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </Field>

            <div className="flex items-center justify-between gap-4 py-1">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('createBonoDialog.labelActive')}
              </label>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs ${
                    form.activo
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-500 dark:text-red-400'
                  }`}
                >
                  {form.activo
                    ? t('createBonoDialog.activeYes')
                    : t('createBonoDialog.activeNo')}
                </span>
                <Switch
                  checked={form.activo}
                  onCheckedChange={(v) => setForm({ ...form, activo: v })}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-row gap-2 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className={`flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('createBonoDialog.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className={`flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {loading
              ? t('createBonoDialog.saving')
              : editingBono
              ? t('createBonoDialog.update')
              : t('createBonoDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};